/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { Drawer, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import adminServices from '../services/adminServices';

const IconTrend = () => (
    <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
        <polyline points="16 7 22 7 22 13" />
    </svg>
);

const IconUsers = () => (
    <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const IconUserPlus = () => (
    <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <line x1="20" y1="8" x2="20" y2="14" />
        <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
);

function StatCard({icon, label, value,sub,subGreen = true}) {
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3.5">
            <div className="flex items-center gap-1 text-gray-500 mb-2">
                {icon}

                <span className="text-xs font-semibold tracking-wider uppercase">
                    {label}
                </span>
            </div>

            <div className="text-2xl font-bold text-gray-900 leading-none mb-1">
                {value ?? '—'}
            </div>

            <div
                className={`text-xs font-medium ${subGreen
                        ? 'text-green-600'
                        : 'text-gray-500'
                    }`}
            >
                {sub}
            </div>
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="animate-pulse">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                {[...Array(3)].map((_, i) => (
                    <div
                        key={i}
                        className="bg-white border border-gray-200 rounded-lg p-3.5 h-24"
                    >
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-3" />
                        <div className="h-7 bg-gray-200 rounded w-1/3 mb-2" />
                        <div className="h-2 bg-gray-200 rounded w-1/2" />
                    </div>
                ))}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-48 mb-4" />
                <div className="h-44 bg-gray-100 rounded" />
            </div>
        </div>
    );
}

const METRIC_OPTIONS = [
    {
        key: 'daily_active_users',
        label: 'Daily Active Users',
        color: '#3B5BDB',
        bucket: 'active',
    },
    {
        key: 'returning_old_users',
        label: 'Returning Old Users',
        color: '#2F9E44',
        bucket: 'returning',
    },
    {
        key: 'new_signups',
        label: 'New Signups',
        color: '#F59F00',
        bucket: 'new',
    },
];

function MetricSelector({
    selectedMetrics,
    onChange,
}) {
    return (
        <div className="flex flex-wrap gap-2">
            {METRIC_OPTIONS.map((metric) => {
                const isSelected =
                    selectedMetrics.includes(
                        metric.key
                    );

                return (
                    <button
                        key={metric.key}
                        type="button"
                        onClick={() =>
                            onChange(metric.key)
                        }
                        aria-pressed={isSelected}
                        className={`
                            inline-flex
                            items-center
                            gap-2
                            px-3
                            py-1.5
                            rounded-lg
                            border
                            text-xs
                            font-medium
                            transition-all
                            duration-150
                            focus:outline-none
                            ${isSelected
                                ? 'bg-gray-50 border-blue-200 text-gray-900 ring-1 ring-blue-100'
                                : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50 hover:border-gray-300'
                            }
                        `}
                    >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{
                                backgroundColor:
                                    metric.color,
                                opacity: isSelected
                                    ? 1
                                    : 0.35,
                            }}
                        />

                        <span>
                            {metric.label}
                        </span>

                        {isSelected && (
                            <span className="text-blue-600 font-bold text-[11px]">
                                ✓
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

function catmullRom(pts) {
    if (pts.length < 2) return '';
    let d = `M${pts[0][0]},${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(i - 1, 0)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(i + 2, pts.length - 1)];
        const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
        const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
        const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
        const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2[0]},${p2[1]}`;
    }
    return d;
}

function ChartTooltip({ visible, style, children }) {
    return (
        <div
            className={`absolute z-50 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-xs pointer-events-none transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0'}`}
            style={style}
        >
            {children}
        </div>
    );
}

function EngagementLineChart({
    rows,
    onPointClick, activePoint, selectedMetrics}) {
    const W = 900;
    const H = 240;
    const PL = 36;
    const PR = 16;
    const PT = 16;
    const PB = 32;

    const chartW = W - PL - PR;
    const chartH = H - PT - PB;

    const [hoverIdx, setHoverIdx] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0 });
    const [clipW, setClipW] = useState(0);

    const svgRef = useRef(null);
    const lineRefs = useRef({});
    const uidRef = useRef(Math.random().toString(36).slice(2, 8));
    const uid = uidRef.current;

    const series = METRIC_OPTIONS.filter((metric) =>
        selectedMetrics.includes(metric.key)
    );

    const n = rows ? rows.length : 0;

    const xPos = (i) =>
        PL + (n <= 1 ? chartW / 2 : (i / (n - 1)) * chartW);

    const maxVal = n
        ? Math.max(
            1,
            ...rows.map((row) =>
                Math.max(
                    ...series.map(
                        (metric) => Number(row[metric.key]) || 0
                    )
                )
            )
        )
        : 1;

    const yPos = (v) =>
        PT + chartH - ((Number(v) || 0) / maxVal) * chartH;

    const seriesPaths = series.map((metric) => {
        const pts = rows.map((row, i) => [xPos(i), yPos(row[metric.key])]);
        const line = catmullRom(pts);
        const area = pts.length
            ? `${line} L${xPos(n - 1)},${PT + chartH} L${xPos(0)},${PT + chartH}Z`
            : '';
        return { ...metric, pts, line, area };
    });

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) =>
        Math.round(maxVal * f)
    );

    // thin out x-axis labels/gridlines as the range grows, same approach
    // used by the other charts in this dashboard
    const MIN_LABEL_GAP = 2;
    const labelEvery = n > 40 ? Math.ceil(n / 16) : 4;
    const visibleIdxs = (() => {
        if (!n) return [];
        const base = rows
            .map((_, i) => i)
            .filter((i) => i % labelEvery === 0);
        const last = n - 1;
        const filtered = base.filter(
            (i) => last - i >= MIN_LABEL_GAP || i === last
        );
        if (filtered[filtered.length - 1] !== last) filtered.push(last);
        return filtered;
    })();

    // animate each line drawing in whenever the row set or the selected
    // metrics change
    useEffect(() => {
        if (!n) return;
        series.forEach((metric) => {
            const el = lineRefs.current[metric.key];
            if (!el) return;
            const len =
                typeof el.getTotalLength === 'function'
                    ? el.getTotalLength()
                    : 1400;
            el.style.transition = 'none';
            el.style.strokeDasharray = `${len}`;
            el.style.strokeDashoffset = `${len}`;
        });

        setClipW(0);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                series.forEach((metric, idx) => {
                    const el = lineRefs.current[metric.key];
                    if (!el) return;
                    el.style.transition = `stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1) ${idx * 120}ms`;
                    el.style.strokeDashoffset = '0';
                });
                setClipW(chartW);
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, selectedMetrics.join(',')]);

    if (!rows || rows.length === 0) {
        return (
            <div className="h-56 flex items-center justify-center text-xs text-gray-400">
                No activity data for the selected period.
            </div>
        );
    }

    if (!selectedMetrics || selectedMetrics.length === 0) {
        return (
            <div className="h-56 flex items-center justify-center text-xs text-gray-400">
                Select at least one metric to view the chart.
            </div>
        );
    }

    const handleMouseMove = (e) => {
        const svg = svgRef.current;
        if (!svg || n === 0) return;

        const rect = svg.getBoundingClientRect();
        const scaleX = W / rect.width;
        const mx = (e.clientX - rect.left) * scaleX;
        const rawIdx = n <= 1 ? 0 : ((mx - PL) / chartW) * (n - 1);
        const idx = Math.max(0, Math.min(n - 1, Math.round(rawIdx)));

        const pxX = (xPos(idx) / W) * rect.width;
        const ys = series.map((metric) => yPos(rows[idx][metric.key]));
        const pxYMin = (Math.min(...ys) / H) * rect.height;

        let left = pxX + 12;
        if (left + 190 > rect.width) left = pxX - 195;

        setTooltipPos({ left, top: Math.max(pxYMin - 10, 0) });
        setHoverIdx(idx);
    };

    const handleClick = (e) => {
        if (hoverIdx === null) return;
        const svg = svgRef.current;
        if (!svg) return;

        const rect = svg.getBoundingClientRect();
        const scaleY = H / rect.height;
        const my = (e.clientY - rect.top) * scaleY;

        // click selects whichever visible series-line is closest to the
        // cursor vertically, at the currently hovered date
        let closest = series[0];
        let bestDist = Infinity;
        series.forEach((metric) => {
            const y = yPos(rows[hoverIdx][metric.key]);
            const dist = Math.abs(y - my);
            if (dist < bestDist) {
                bestDist = dist;
                closest = metric;
            }
        });

        onPointClick({
            date: rows[hoverIdx].activity_date,
            bucket: closest.bucket,
            label: closest.label,
            value: rows[hoverIdx][closest.key],
        });
    };

    const activeIdx = activePoint
        ? rows.findIndex((row) => row.activity_date === activePoint.date)
        : -1;
    const activeMetric = activePoint
        ? METRIC_OPTIONS.find((metric) => metric.bucket === activePoint.bucket)
        : null;

    return (
        <div className="w-full">
            <div className="flex flex-wrap gap-4 mb-2">
                {series.map((metric) => (
                    <div
                        key={metric.key}
                        className="flex items-center gap-1.5 text-xs text-gray-500"
                    >
                        <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: metric.color }}
                        />
                        {metric.label}
                    </div>
                ))}
            </div>

            <div className="relative">
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${W} ${H}`}
                    preserveAspectRatio="none"
                    className="w-full h-56 overflow-visible block cursor-crosshair"
                    onMouseMove={handleMouseMove}
                    onMouseLeave={() => setHoverIdx(null)}
                    onClick={handleClick}
                >
                    <defs>
                        {seriesPaths.map((metric) => (
                            <linearGradient
                                key={metric.key}
                                id={`eg-${metric.key}-${uid}`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop offset="0%" stopColor={metric.color} stopOpacity="0.14" />
                                <stop offset="100%" stopColor={metric.color} stopOpacity="0.01" />
                            </linearGradient>
                        ))}
                        <clipPath id={`eclip-${uid}`}>
                            <rect
                                x={PL}
                                y={PT - 4}
                                width={clipW}
                                height={chartH + 8}
                                style={{
                                    transition:
                                        'width 1.2s cubic-bezier(0.4,0,0.2,1)',
                                }}
                            />
                        </clipPath>
                    </defs>

                    {yTicks.map((v) => (
                        <g key={v}>
                            <line
                                x1={PL}
                                y1={yPos(v)}
                                x2={W - PR}
                                y2={yPos(v)}
                                stroke="#e5e7eb"
                                strokeWidth="1"
                                strokeDasharray="4 4"
                            />
                            <text
                                x={PL - 6}
                                y={yPos(v) + 3}
                                textAnchor="end"
                                fontSize="9"
                                fill="#9ca3af"
                            >
                                {v}
                            </text>
                        </g>
                    ))}

                    {visibleIdxs.map((i) => (
                        <line
                            key={`vg${i}`}
                            x1={xPos(i)}
                            y1={PT}
                            x2={xPos(i)}
                            y2={PT + chartH}
                            stroke="#e5e7eb"
                            strokeWidth="1"
                            strokeDasharray="4 4"
                        />
                    ))}

                    {visibleIdxs.map((i) => (
                        <text
                            key={`lbl${i}`}
                            x={xPos(i)}
                            y={H - 8}
                            textAnchor="middle"
                            fontSize="10"
                            fill="#9ca3af"
                        >
                            {moment.utc(rows[i].activity_date).format('DD MMM')}
                        </text>
                    ))}

                    {seriesPaths.map((metric) => (
                        <g key={`area-${metric.key}`} clipPath={`url(#eclip-${uid})`}>
                            <path d={metric.area} fill={`url(#eg-${metric.key}-${uid})`} />
                        </g>
                    ))}

                    {seriesPaths.map((metric) => (
                        <path
                            key={`line-${metric.key}`}
                            ref={(el) => (lineRefs.current[metric.key] = el)}
                            d={metric.line}
                            fill="none"
                            stroke={metric.color}
                            strokeWidth="1.8"
                        />
                    ))}

                    {hoverIdx !== null && (
                        <g>
                            <line
                                x1={xPos(hoverIdx)}
                                y1={PT}
                                x2={xPos(hoverIdx)}
                                y2={PT + chartH}
                                stroke="#d1d5db"
                                strokeWidth="1"
                                strokeDasharray="3 3"
                            />
                            {series.map((metric) => (
                                <circle
                                    key={`hover-${metric.key}`}
                                    cx={xPos(hoverIdx)}
                                    cy={yPos(rows[hoverIdx][metric.key])}
                                    r="3.5"
                                    fill="#fff"
                                    stroke={metric.color}
                                    strokeWidth="2"
                                />
                            ))}
                        </g>
                    )}

                    {activeIdx !== -1 && activeMetric && (
                        <circle
                            cx={xPos(activeIdx)}
                            cy={yPos(rows[activeIdx][activeMetric.key])}
                            r="5.5"
                            fill={activeMetric.color}
                            stroke="#fff"
                            strokeWidth="2"
                        />
                    )}
                </svg>

                {hoverIdx !== null && (
                    <ChartTooltip
                        visible
                        style={{ left: tooltipPos.left, top: tooltipPos.top }}
                    >
                        <div className="font-semibold text-gray-800 mb-1 whitespace-nowrap">
                            {moment.utc(rows[hoverIdx].activity_date).format('DD MMM YYYY')}
                        </div>
                        {series.map((metric) => (
                            <div
                                key={metric.key}
                                className="flex items-center gap-1 mt-0.5 whitespace-nowrap"
                            >
                                <span className="font-medium" style={{ color: metric.color }}>
                                    {metric.label}:
                                </span>
                                <span className="text-gray-600">
                                    {rows[hoverIdx][metric.key] ?? 0}
                                </span>
                            </div>
                        ))}
                        <div className="text-[10px] text-gray-400 mt-1 whitespace-nowrap">
                            Click near a line to see its users
                        </div>
                    </ChartTooltip>
                )}
            </div>
        </div>
    );
}

const ENGAGEMENT_USERS_PAGE_SIZE = 10;

function EngagementUsersPanel({
    selection,
    onClose,
    onSelectUser,
}) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] =
        useState(false);
    const [error, setError] =
        useState('');
    const [page, setPage] =
        useState(1);

    useEffect(() => {
        if (!selection) {
            setUsers([]);
            return;
        }

        let cancelled = false;

        (async () => {
            setLoading(true);
            setError('');
            setPage(1);

            try {
                const res =
                    await adminServices.getEngagementUsersByDate(
                        selection.date,
                        selection.bucket,
                        selection.period
                    );

                if (!cancelled) {
                    setUsers(
                        res?.data?.users ||
                        []
                    );
                }
            } catch (err) {
                console.error(
                    err,
                    'failed to [getEngagementUsersByDate]'
                );

                if (!cancelled) {
                    setError(
                        'Failed to load users.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(
                        false
                    );
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        selection?.date,
        selection?.bucket,
        selection?.period,
    ]);

    if (!selection) {
        return null;
    }

    const totalPages =
        Math.max(
            Math.ceil(
                users.length /
                ENGAGEMENT_USERS_PAGE_SIZE
            ),
            1
        );

    const currentPage =
        Math.min(
            page,
            totalPages
        );

    const start =
        (currentPage - 1) *
        ENGAGEMENT_USERS_PAGE_SIZE;

    const pageUsers =
        users.slice(
            start,
            start +
            ENGAGEMENT_USERS_PAGE_SIZE
        );

    return (
        <div className="bg-white border border-blue-200 rounded-lg overflow-hidden mb-4 ring-1 ring-blue-100">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <div>
                    <div className="text-sm font-bold text-gray-900">
                        {selection.label}{' '}
                        <span className="text-gray-400 font-normal">
                            ·{' '}
                            {moment.utc(
                                selection.date
                            ).format(
                                'DD MMM YYYY'
                            )}
                        </span>
                    </div>

                    <div className="text-xs text-gray-400 mt-0.5">
                        {users.length}{' '}
                        users
                    </div>
                </div>

                <button
                    onClick={onClose}
                    className="text-xs text-gray-400 hover:text-gray-700 px-2 py-1"
                >
                    ✕ Close
                </button>
            </div>
            {loading ? (
                <div className="py-8 text-center text-xs text-gray-400">
                    Loading users…
                </div>
            ) : error ? (
                <div className="py-8 text-center text-xs text-red-600">
                    {error}
                </div>
            ) : (
                <>
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-400 border-b border-gray-100">
                                    Name
                                </th>

                                <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-400 border-b border-gray-100">
                                    Email
                                </th>
                                <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-400 border-b border-gray-100">
                                    Joined
                                </th>

                                <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-400 border-b border-gray-100">
                                    Status
                                </th>

                                <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-400 border-b border-gray-100">
                                    Assessments Attended
                                </th>

                                <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-400 border-b border-gray-100">
                                    Action
                                </th>
                               
                            </tr>
                        </thead>

                        <tbody>
                            {pageUsers.length >
                                0 ? (
                                pageUsers.map(
                                    (user) => (
                                        <tr
                                            key={
                                                user.id
                                            }
                                            className="border-b border-gray-50 hover:bg-gray-50"
                                        >
                                            <td className="px-4 py-2.5 text-xs text-gray-900 font-medium">
                                                {user.name ||
                                                    '—'}
                                            </td>

                                            <td className="px-4 py-2.5 text-xs text-gray-700">
                                                {user.email ||
                                                    '—'}
                                            </td>
                                             <td className="px-4 py-2.5 text-xs text-gray-700">
                                                {moment.utc(user?.createDateTime).format("DD MMM YYYY") ||
                                                    '—'}
                                            </td>

                                            <td className="px-4 py-2.5 text-xs">
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded font-medium ${user.status ===
                                                            'Active'
                                                            ? 'text-green-600 bg-green-50'
                                                            : 'text-gray-500 bg-gray-100'
                                                        }`}
                                                >
                                                    {user.status ||
                                                        '—'}
                                                </span>
                                            </td>

                                            <td className="px-4 py-2.5 text-xs text-right">
                                                {user.assessmentsAttended ??
                                                    0}{' '}
                                                {/* (
                                                {user.assessmentAttemptCount ??
                                                    0}{' '}
                                                attempts) */}
                                            </td>

                                            <td className="px-4 py-2.5 text-right">
                                                <button
                                                    onClick={() =>
                                                        onSelectUser(
                                                            user
                                                        )
                                                    }
                                                    disabled={
                                                        Number(
                                                            user.assessmentAttemptCount
                                                        ) ===
                                                        0
                                                    }
                                                    className="text-xs border border-gray-200 text-gray-500 rounded px-3 py-1.5 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    View history
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                )
                            ) : (
                                <tr>
                                    <td
                                        colSpan={
                                            5
                                        }
                                        className="px-4 py-6 text-center text-xs text-gray-400"
                                    >
                                        No users
                                        found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {totalPages >
                        1 && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
                                <span className="text-xs text-gray-400">
                                    Page{' '}
                                    {
                                        currentPage
                                    }{' '}
                                    /{' '}
                                    {
                                        totalPages
                                    }
                                </span>

                                <div className="flex gap-2">
                                    <button
                                        disabled={
                                            currentPage <=
                                            1
                                        }
                                        onClick={() =>
                                            setPage(
                                                currentPage -
                                                1
                                            )
                                        }
                                        className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-white transition-colors"
                                    >
                                        Previous
                                    </button>

                                    <button
                                        disabled={
                                            currentPage >=
                                            totalPages
                                        }
                                        onClick={() =>
                                            setPage(
                                                currentPage +
                                                1
                                            )
                                        }
                                        className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-white transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                </>
            )}
        </div>
    );
}

function UserHistoryDrawer({
    userId,
    onClose,
}) {
    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState('');

    const [data, setData] =
        useState(null);

    useEffect(() => {
        if (!userId) {
            setData(null);
            return;
        }

        let cancelled = false;

        (async () => {
            setLoading(true);
            setError('');

            try {
                const res =
                    await adminServices.getIndividualDetailsByAdmin(
                        userId
                    );

                if (!cancelled) {
                    setData(
                        res?.data
                    );
                }
            } catch (err) {
                console.error(
                    err,
                    'failed to [getUserAssessmentHistory]'
                );

                if (!cancelled) {
                    setError(
                        'Failed to load attempt history.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(
                        false
                    );
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [userId]);

    return (
        <Drawer
            anchor="right"
            open={!!userId}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: {
                        xs: '100vw',
                        sm: '460px',
                    },
                    borderLeft:
                        '1px solid #e5e7eb',
                    boxShadow:
                        '-4px 0 24px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection:
                        'column',
                },
            }}
        >
            {userId && (
                <>
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                        <div>
                            <div className="text-xs font-semibold uppercase text-gray-400">
                                Attempt History
                            </div>

                            

                            <div className="text-xs text-gray-400">
                                {
                                    data
                                        ?.user
                                        ?.email
                                }
                            </div>
                        </div>

                        <IconButton
                            size="small"
                            onClick={
                                onClose
                            }
                            sx={{
                                color: '#6b7280',
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>

                    <div className="px-6 py-5 flex-1 overflow-y-auto">
                        {loading && (
                            <div className="py-10 text-center text-xs text-gray-400">
                                Loading…
                            </div>
                        )}

                        {!loading &&
                            error && (
                                <div className="py-10 text-center text-xs text-red-600">
                                    {
                                        error
                                    }
                                </div>
                            )}

                        {!loading &&
                            !error &&
                            (!data ||
                                data.length ===
                                0) && (
                                <div className="py-10 text-center text-xs text-gray-400">
                                    No attempts
                                    recorded.
                                </div>
                            )}

                        {!loading &&
                            !error &&
                            data?.length >
                            0 &&
                            (() => {
                                const assessments =
                                    data.flatMap(
                                        (
                                            subscription
                                        ) =>
                                            subscription.assessments ||
                                            []
                                    );

                                const attemptedAssessments = assessments.filter( (assessment) =>
                                            assessment
                                                .attempts_accuracy
                                                ?.length >
                                            0
                                    );

                                if (attemptedAssessments.length === 0) {
                                    return (
                                        <div className="py-10 text-center text-xs text-gray-400">
                                            No attempts
                                            recorded.
                                        </div>
                                    );
                                }

                                return (
                                    <div className="flex flex-col gap-2">
                                        {attemptedAssessments.map((assessment) => (
                                                <div
                                                    key={
                                                        assessment.id
                                                    }
                                                    className="border border-gray-100 rounded-lg p-3"
                                                >
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="text-xs font-semibold text-gray-900">
                                                            {
                                                                assessment.title
                                                            }
                                                        </div>

                                                        <div className="text-[10px] text-gray-400">
                                                            {
                                                                assessment.type
                                                            }
                                                        </div>
                                                    </div>

                                                    <div className="space-y-1.5">
                                                        {assessment.attempts_accuracy.map(
                                                            (
                                                                attempt,
                                                                index
                                                            ) => (
                                                                <div
                                                                    key={`${assessment.id}-${attempt.attempt_no}-${index}`}
                                                                    className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2"
                                                                >
                                                                    <div className="text-[11px] text-gray-500">
                                                                        <span className="font-medium text-gray-700">
                                                                            Attempt{' '}
                                                                            {
                                                                                attempt.attempt_no
                                                                            }
                                                                        </span>

                                                                        <span className="mx-1">
                                                                            ·
                                                                        </span>

                                                                        <span>
                                                                            {attempt.date_completed ? moment.utc(attempt.date_completed).format('DD MMM YYYY'
                                                                                )
                                                                                : 'In progress'}
                                                                        </span>

                                                                        <span className="mx-1">
                                                                            ·
                                                                        </span>

                                                                        <span>
                                                                            {attempt.attended ||
                                                                                0}
                                                                            /
                                                                            {attempt.total_questions ||
                                                                                0}
                                                                        </span>
                                                                    </div>

                                                                    <div className="text-xs font-semibold text-gray-700">
                                                                        {attempt.accuracy ??
                                                                            0}
                                                                        %
                                                                    </div>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            })()}
                    </div>
                </>
            )}
        </Drawer>
    );
}

export default function Engagement({period}) {
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selection, setSelection] = useState(null);
    const [historyUserId, setHistoryUserId] = useState(null);
    const [selectedMetrics,setSelectedMetrics] = useState(['daily_active_users','returning_old_users','new_signups',]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            setSelection(null);
            try {
                const days = Number(period);
                const res = await adminServices.getDashboardEngagement(days);
                if (cancelled) {
                    return;
                }
                setRows(res?.data?.data ||[]);
                setTotals(res?.data?.totals || null);
            } catch (err) {
                console.error(
                    err,
                    'failed to [getDashboardEngagement]'
                );

                if (!cancelled) {
                    setRows([]);
                    setTotals(null);
                    setError(
                        'Failed to load engagement data.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(
                        false
                    );
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [period]);

    const formatDateIST = (date) => {
        return new Intl.DateTimeFormat(
            'en-CA',
            {
                timeZone:
                    'Asia/Kolkata',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }
        ).format(
            new Date(date)
        );
    };

    const handleMetricChange = (metricKey) => {
        setSelectedMetrics(
            (previous) => {
               
                if (
                    previous.includes(
                        metricKey
                    )
                ) {
                    if (
                        previous.length ===
                        1
                    ) {
                        return previous;
                    }

                    return previous.filter(
                        (key) =>
                            key !==
                            metricKey
                    );
                }
                return [
                    ...previous,
                    metricKey,
                ];
            }
        );
        setSelection(null);
    };

    if (loading) {
        return (
            <LoadingSkeleton />
        );
    }

    return (
        <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <StatCard
                    icon={
                        <IconTrend />
                    }
                    label="Daily Active Users"
                    value={
                        totals?.dau
                    }
                    sub={`total · last ${period}d`}
                />

                <StatCard
                    icon={
                        <IconUsers />
                    }
                    label="Returning Old Users"
                    value={
                        totals?.returning
                    }
                    sub="came back to log in"
                />

                <StatCard
                    icon={
                        <IconUserPlus />
                    }
                    label="New Signups"
                    value={
                        totals?.newSignups
                    }
                    sub={`in last ${period}d`}
                    subGreen={false}
                />

                   <StatCard
                    icon={
                        <IconUserPlus />
                    }
                    label="Active Signups"
                    value={
                        totals?.activeSignups
                    }
                    sub={`in last ${period}d`}
                    subGreen={false}
                />
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                <div className="mb-4">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                        SELECT METRIC
                    </div>

                    <MetricSelector
                        selectedMetrics={
                            selectedMetrics
                        }
                        onChange={
                            handleMetricChange
                        }
                    />
                </div>

                <div className="text-sm font-bold text-gray-900 mb-0.5">
                    Activity Over Time
                </div>

                <div className="text-xs text-gray-400 mb-3">
                    Hover a point for details,
                    click near a line to see the user list
                </div>

                {error ? (
                    <div className="py-8 text-center text-xs text-red-600">
                        {error}
                    </div>
                ) : (
                    <EngagementLineChart rows={rows}
                        activePoint={selection}
                        selectedMetrics={
                            selectedMetrics
                        }
                        onPointClick={(
                            point
                        ) => {
                            setSelection(
                                {
                                    ...point,
                                    date: formatDateIST(
                                        point.date
                                    ),
                                    period,
                                }
                            );
                        }}
                    />
                )}
            </div>

            <EngagementUsersPanel selection={selection} onClose={() => setSelection(null)} onSelectUser={(user) => setHistoryUserId(user.id)} />
            <UserHistoryDrawer userId={historyUserId} onClose={() => setHistoryUserId(null)}
            />
        </>
    );
}